import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDate } from "@/lib/api";
import { COMPANY } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export function toWaNumber(phone, cc) {
  let d = (phone || "").replace(/\D/g, "").replace(/^00/, "");
  if (d.startsWith("0")) d = cc ? cc + d.replace(/^0+/, "") : "";
  return d.length >= 8 && d.length <= 15 ? d : "";
}

export const ConfirmVisitDialog = ({ open, onClose, insp, onSent }) => {
  const { user } = useAuth();
  const [cc, setCc] = useState("");
  const [number, setNumber] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { data: s } = await api.get("/settings");
      let agentPhone = user.role === "agent" ? user.phone : "";
      if (user.role === "admin") {
        const { data: agents } = await api.get("/agents");
        agentPhone = agents.find((a) => a.id === insp.agent_id)?.phone || "";
      }
      setCc(s.country_code || "");
      setNumber(toWaNumber(insp.customer.phone, s.country_code));
      setMsg(`مرحباً ${insp.customer.name}،\nنؤكد لكم موعد زيارة المعاينة ورفع المقاسات من ${COMPANY}:\nالموعد: ${fmtDate(insp.scheduled_at)}\nالمندوب: ${insp.agent_name}${agentPhone ? ` - ${agentPhone}` : ""}\nرقم الطلب: ${insp.ref}\nنرجو التأكد من تواجد شخص في الموقع. شكراً لكم.`);
    })().catch((e) => toast.error(errMsg(e)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCc = (v) => {
    setCc(v);
    setNumber(toWaNumber(insp.customer.phone, v));
  };

  const send = () => {
    if (!insp.scheduled_at) return toast.error("حدّد موعد الزيارة أولاً");
    const n = toWaNumber(number, cc);
    if (!n) return toast.error("رقم العميل غير صالح؛ أدخله مع رمز الدولة");
    window.open(`https://wa.me/${n}?text=${encodeURIComponent(msg)}`, "_blank");
    if (user.role === "admin" && /^\d{1,4}$/.test(cc)) api.put("/settings", { country_code: cc }).catch(() => {});
    api.post(`/inspections/${insp.id}/confirmation-sent`).then((r) => onSent?.(r.data)).catch(() => {});
    onClose();
  };

  const local = (insp.customer.phone || "").replace(/\D/g, "").startsWith("0");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="confirm-visit-dialog">
        <DialogHeader><DialogTitle className="text-start font-heading">تأكيد الموعد للعميل عبر واتساب</DialogTitle></DialogHeader>
        {!insp.scheduled_at && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="confirm-no-schedule">لم يُحدَّد موعد الزيارة بعد.</p>}
        {insp.confirmation_sent_at && <p className="text-xs text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14} />أُرسل تأكيد سابق في {fmtDate(insp.confirmation_sent_at)}</p>}
        <div className="grid grid-cols-3 gap-2">
          {local && (
            <Input value={cc} onChange={(e) => saveCc(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="رمز الدولة"
              disabled={user.role !== "admin" && !!cc} dir="ltr" className="text-left" data-testid="confirm-country-code" />
          )}
          <Input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="رقم العميل الدولي" dir="ltr" className={`text-left ${local ? "col-span-2" : "col-span-3"}`} data-testid="confirm-phone-input" />
        </div>
        {local && !cc && <p className="text-xs text-slate-500">رقم العميل محلي؛ أدخل رمز الدولة مرة واحدة (مثل 966) ويُحفظ للمرات القادمة.</p>}
        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={7} data-testid="confirm-message-input" />
        <button onClick={send} disabled={!insp.scheduled_at} data-testid="confirm-send-whatsapp" className="h-12 rounded-lg bg-[#25D366] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#1EB257] disabled:opacity-50">
          <MessageCircle size={18} />إرسال التأكيد للعميل
        </button>
      </DialogContent>
    </Dialog>
  );
};
